/**
 * World Map View Component
 * 
 * Renders the world map with terrain, cities, and allows interaction
 */

import { getImagePath } from '../utils/imagePath.js';

export class WorldMapView {
    constructor(containerElement, worldMapManager, callbacks = {}) {
        this.container = containerElement;
        this.worldMapManager = worldMapManager;
        this.callbacks = callbacks;
        
        // Canvas for map rendering
        this.canvas = null;
        this.ctx = null;
        
        // View state
        this.zoom = 1;
        this.panX = 0;
        this.panY = 0;
        this.isDragging = false;
        this.dragStartX = 0;
        this.dragStartY = 0;
        
        // Rendering state
        this.loadedRegions = new Map();
        this.cities = [];
        this.selectedCity = null;
        this.hoveredTile = null;
        
        // Tile size (pixels per tile)
        this.baseTileSize = 8; // Base size at zoom 1
        this.minZoom = 0.5;
        this.maxZoom = 4;
        
        // Terrain colors
        this.terrainColors = {
            plains: '#8B9A5B',
            forest: '#4A5D23',
            mountain: '#6B6B6B',
            water: '#4A90E2',
            ocean: '#2E5C8A'
        };
    }
    
    /**
     * Initialize the world map view
     */
    async init() {
        this.createCanvas();
        this.attachEventListeners();
        await this.loadInitialData();
        this.render();
        this.startAutoUpdate();
    }
    
    /**
     * Create canvas element
     */
    createCanvas() {
        this.canvas = document.createElement('canvas');
        this.canvas.id = 'world-map-canvas';
        this.canvas.style.width = '100%';
        this.canvas.style.height = '100%';
        this.canvas.style.cursor = 'grab';
        this.canvas.style.imageRendering = 'pixelated';
        
        // Set actual canvas size
        this.resizeCanvas();
        
        this.ctx = this.canvas.getContext('2d');
        this.container.appendChild(this.canvas);
        
        // Handle window resize
        window.addEventListener('resize', () => this.resizeCanvas());
    }
    
    /**
     * Resize canvas to container
     */
    resizeCanvas() {
        const rect = this.container.getBoundingClientRect();
        this.canvas.width = rect.width;
        this.canvas.height = rect.height;
        this.render();
    }
    
    /**
     * Load initial map data
     */
    async loadInitialData() {
        if (!this.worldMapManager.currentWorld) {
            await this.worldMapManager.initialize(1);
        }
        
        // Load initial viewport
        await this.loadViewport();
        
        // Load cities
        this.cities = this.worldMapManager.cities || [];
    }
    
    /**
     * Load regions for current viewport
     */
    async loadViewport() {
        const viewX = Math.floor(this.panX);
        const viewY = Math.floor(this.panY);
        const viewWidth = Math.ceil(this.canvas.width / (this.baseTileSize * this.zoom)) + 2;
        const viewHeight = Math.ceil(this.canvas.height / (this.baseTileSize * this.zoom)) + 2;
        
        const regions = await this.worldMapManager.loadRegions(
            viewX, viewY, viewWidth, viewHeight
        );
        
        // Cache regions
        regions.forEach(region => {
            const key = `${region.region_x}_${region.region_y}`;
            this.loadedRegions.set(key, region);
        });
        
        // Update world map manager viewport
        this.worldMapManager.setViewport(viewX, viewY, viewWidth, viewHeight, this.zoom);
    }
    
    /**
     * Render the map
     */
    render() {
        if (!this.ctx || !this.worldMapManager.currentWorld) return;
        
        const ctx = this.ctx;
        const tileSize = this.baseTileSize * this.zoom;
        
        // Clear canvas
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        
        // Calculate visible tile range
        const startX = Math.floor(this.panX);
        const startY = Math.floor(this.panY);
        const endX = Math.ceil(startX + this.canvas.width / tileSize);
        const endY = Math.ceil(startY + this.canvas.height / tileSize);
        
        // Draw terrain
        for (let y = startY; y < endY; y++) {
            for (let x = startX; x < endX; x++) {
                const screenX = (x - this.panX) * tileSize;
                const screenY = (y - this.panY) * tileSize;
                
                // Get region
                const region = this.worldMapManager.getRegion(x, y);
                const terrainType = region?.terrain_type || 'ocean';
                const color = this.terrainColors[terrainType] || '#000000';
                
                // Draw tile
                ctx.fillStyle = color;
                ctx.fillRect(screenX, screenY, tileSize, tileSize);
                
                // Draw border at low zoom
                if (this.zoom >= 2) {
                    ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
                    ctx.lineWidth = 0.5;
                    ctx.strokeRect(screenX, screenY, tileSize, tileSize);
                }
            }
        }
        
        // Draw cities
        this.renderCities(tileSize, startX, startY, endX, endY);
        
        // Draw hovered tile highlight
        if (this.hoveredTile) {
            this.renderHoveredTile(tileSize);
        }
        
        // Draw grid at high zoom
        if (this.zoom >= 3) {
            this.renderGrid(tileSize, startX, startY, endX, endY);
        }
        
        // Draw UI overlay
        this.renderUI();
    }
    
    /**
     * Render cities on the map
     */
    renderCities(tileSize, startX, startY, endX, endY) {
        const ctx = this.ctx;
        const visibleCities = this.cities.filter(city => {
            return city.world_x >= startX && city.world_x < endX &&
                   city.world_y >= startY && city.world_y < endY;
        });
        
        visibleCities.forEach(city => {
            const screenX = (city.world_x - this.panX) * tileSize;
            const screenY = (city.world_y - this.panY) * tileSize;
            
            // Draw city marker
            const isSelected = this.selectedCity?.id === city.id;
            const size = tileSize * 0.8;
            
            ctx.fillStyle = isSelected ? '#FFD700' : '#C7A876';
            ctx.beginPath();
            ctx.arc(screenX + tileSize / 2, screenY + tileSize / 2, size / 2, 0, Math.PI * 2);
            ctx.fill();
            
            // Draw border
            ctx.strokeStyle = isSelected ? '#FFA500' : '#8B6F47';
            ctx.lineWidth = 2;
            ctx.stroke();
            
            // Draw city name at higher zoom
            if (this.zoom >= 2 && city.name) {
                ctx.fillStyle = '#FFFFFF';
                ctx.font = `${Math.max(10, tileSize * 0.3)}px Arial`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'top';
                ctx.fillText(city.name, screenX + tileSize / 2, screenY + tileSize + 2);
            }
        });
    }
    
    /**
     * Render hovered tile highlight
     */
    renderHoveredTile(tileSize) {
        const ctx = this.ctx;
        const { x, y } = this.hoveredTile;
        
        const screenX = (x - this.panX) * tileSize;
        const screenY = (y - this.panY) * tileSize;
        
        // Draw highlight
        ctx.strokeStyle = '#FFD700';
        ctx.lineWidth = 2;
        ctx.strokeRect(screenX, screenY, tileSize, tileSize);
        
        // Draw placement indicator if valid
        if (this.worldMapManager.canPlaceCity(x, y)) {
            ctx.fillStyle = 'rgba(0, 255, 0, 0.3)';
            ctx.fillRect(screenX, screenY, tileSize, tileSize);
        } else {
            ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
            ctx.fillRect(screenX, screenY, tileSize, tileSize);
        }
    }
    
    /**
     * Render grid lines
     */
    renderGrid(tileSize, startX, startY, endX, endY) {
        const ctx = this.ctx;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.lineWidth = 0.5;
        
        // Vertical lines
        for (let x = startX; x <= endX; x++) {
            const screenX = (x - this.panX) * tileSize;
            ctx.beginPath();
            ctx.moveTo(screenX, 0);
            ctx.lineTo(screenX, this.canvas.height);
            ctx.stroke();
        }
        
        // Horizontal lines
        for (let y = startY; y <= endY; y++) {
            const screenY = (y - this.panY) * tileSize;
            ctx.beginPath();
            ctx.moveTo(0, screenY);
            ctx.lineTo(this.canvas.width, screenY);
            ctx.stroke();
        }
    }
    
    /**
     * Render UI overlay (zoom, coordinates, etc.)
     */
    renderUI() {
        const ctx = this.ctx;
        
        // Draw coordinate info
        if (this.hoveredTile) {
            const { x, y } = this.hoveredTile;
            const region = this.worldMapManager.getRegion(x, y);
            const continent = this.worldMapManager.getContinent(x, y);
            
            ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
            ctx.fillRect(10, 10, 200, 100);
            
            ctx.fillStyle = '#FFFFFF';
            ctx.font = '12px Arial';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
            ctx.fillText(`Coordinates: (${x}, ${y})`, 15, 15);
            ctx.fillText(`Terrain: ${region?.terrain_type || 'unknown'}`, 15, 30);
            ctx.fillText(`Continent: ${continent ? `${continent.continent_x}, ${continent.continent_y}` : 'Ocean'}`, 15, 45);
            ctx.fillText(`Zoom: ${this.zoom.toFixed(2)}x`, 15, 60);
            ctx.fillText(`Cities: ${this.cities.length}`, 15, 75);
        }
    }
    
    /**
     * Attach event listeners
     */
    attachEventListeners() {
        // Mouse wheel for zoom
        this.canvas.addEventListener('wheel', (e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? 0.9 : 1.1;
            this.setZoom(this.zoom * delta, e.offsetX, e.offsetY);
        });
        
        // Mouse down for dragging
        this.canvas.addEventListener('mousedown', (e) => {
            this.isDragging = true;
            this.canvas.style.cursor = 'grabbing';
            this.dragStartX = e.clientX - this.panX * this.baseTileSize * this.zoom;
            this.dragStartY = e.clientY - this.panY * this.baseTileSize * this.zoom;
        });
        
        // Mouse move for dragging and hover
        this.canvas.addEventListener('mousemove', (e) => {
            const rect = this.canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            
            // Update hovered tile
            const tileSize = this.baseTileSize * this.zoom;
            const worldX = Math.floor(this.panX + mouseX / tileSize);
            const worldY = Math.floor(this.panY + mouseY / tileSize);
            this.hoveredTile = { x: worldX, y: worldY };
            
            // Handle dragging
            if (this.isDragging) {
                this.panX = (e.clientX - this.dragStartX) / (this.baseTileSize * this.zoom);
                this.panY = (e.clientY - this.dragStartY) / (this.baseTileSize * this.zoom);
                this.clampPan();
                this.render();
            } else {
                this.render(); // Re-render for hover highlight
            }
        });
        
        // Mouse up
        this.canvas.addEventListener('mouseup', () => {
            this.isDragging = false;
            this.canvas.style.cursor = 'grab';
        });
        
        // Mouse leave
        this.canvas.addEventListener('mouseleave', () => {
            this.isDragging = false;
            this.hoveredTile = null;
            this.render();
        });
        
        // Click for city selection/placement
        this.canvas.addEventListener('click', (e) => {
            if (this.isDragging) return; // Ignore clicks during drag
            
            const rect = this.canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            
            const tileSize = this.baseTileSize * this.zoom;
            const worldX = Math.floor(this.panX + mouseX / tileSize);
            const worldY = Math.floor(this.panY + mouseY / tileSize);
            
            // Check if clicked on a city
            const clickedCity = this.cities.find(city => 
                city.world_x === worldX && city.world_y === worldY
            );
            
            if (clickedCity) {
                this.selectCity(clickedCity);
            } else if (this.callbacks.onTileClick) {
                this.callbacks.onTileClick(worldX, worldY);
            }
        });
    }
    
    /**
     * Set zoom level
     */
    setZoom(newZoom, centerX = null, centerY = null) {
        const oldZoom = this.zoom;
        this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, newZoom));
        
        // Zoom towards mouse position
        if (centerX !== null && centerY !== null && oldZoom !== this.zoom) {
            const zoomFactor = this.zoom / oldZoom;
            const worldX = this.panX + centerX / (this.baseTileSize * oldZoom);
            const worldY = this.panY + centerY / (this.baseTileSize * oldZoom);
            
            this.panX = worldX - centerX / (this.baseTileSize * this.zoom);
            this.panY = worldY - centerY / (this.baseTileSize * this.zoom);
        }
        
        this.clampPan();
        this.loadViewport().then(() => this.render());
    }
    
    /**
     * Clamp pan to world bounds
     */
    clampPan() {
        if (!this.worldMapManager.currentWorld) return;
        
        const world = this.worldMapManager.currentWorld;
        const maxX = world.width - this.canvas.width / (this.baseTileSize * this.zoom);
        const maxY = world.height - this.canvas.height / (this.baseTileSize * this.zoom);
        
        this.panX = Math.max(0, Math.min(maxX, this.panX));
        this.panY = Math.max(0, Math.min(maxY, this.panY));
    }
    
    /**
     * Select a city
     */
    selectCity(city) {
        this.selectedCity = city;
        this.render();
        
        if (this.callbacks.onCitySelect) {
            this.callbacks.onCitySelect(city);
        }
    }
    
    /**
     * Center view on coordinates
     */
    centerOn(x, y) {
        const tileSize = this.baseTileSize * this.zoom;
        this.panX = x - this.canvas.width / (2 * tileSize);
        this.panY = y - this.canvas.height / (2 * tileSize);
        this.clampPan();
        this.loadViewport().then(() => this.render());
    }
    
    /**
     * Center view on city
     */
    centerOnCity(city) {
        this.centerOn(city.world_x, city.world_y);
        this.selectCity(city);
    }
    
    /**
     * Start auto-update
     */
    startAutoUpdate() {
        setInterval(() => {
            // Reload cities periodically
            this.cities = this.worldMapManager.cities || [];
            this.render();
        }, 5000); // Update every 5 seconds
    }
    
    /**
     * Cleanup
     */
    destroy() {
        if (this.canvas && this.canvas.parentNode) {
            this.canvas.parentNode.removeChild(this.canvas);
        }
    }
}

